import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Payment } from "../types/features/administration/Transaction";

/**
 * Génère un reçu PDF pour une transaction
 * @param transaction - Les données de la transaction
 */
export const generateReceiptPDF = async (transaction: Payment) => {
  const doc = new jsPDF();

  // Charger le logo
  const logo = await loadImage("/EASYPAY_logo.png");

  // Ajouter le logo
  if (logo) {
    doc.addImage(logo, "PNG", 15, 10, 40, 15);
  }

  // En-tête de l'entreprise
  doc.setFontSize(20);
  doc.setTextColor(41, 128, 185);
  doc.text("IZILIFEPAY", 105, 20, { align: "center" });

  doc.setFontSize(10);
  doc.setTextColor(100, 100, 100);
  doc.text("Reçu de Transaction", 105, 27, { align: "center" });

  // Ligne de séparation
  doc.setDrawColor(200, 200, 200);
  doc.line(15, 32, 195, 32);

  // Informations de la transaction
  let yPos = 40;

  // Titre
  doc.setFontSize(14);
  doc.setTextColor(0, 0, 0);
  doc.text(`Reçu #${transaction.id.slice(-8).toUpperCase()}`, 15, yPos);

  yPos += 10;

  // Date
  doc.setFontSize(10);
  doc.setTextColor(100, 100, 100);
  const date = transaction.created_at
    ? new Date(transaction.created_at).toLocaleString("fr-FR", {
        day: "2-digit",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "N/A";
  doc.text(`Date: ${date}`, 15, yPos);

  yPos += 15;

  // Statut avec couleur
  doc.setFontSize(12);
  const statusColor = getStatusColor(transaction.status);
  doc.setTextColor(statusColor.r, statusColor.g, statusColor.b);
  doc.text(`Statut: ${transaction.status || "N/A"}`, 15, yPos);

  yPos += 15;

  // Section Montants (encadré)
  doc.setFillColor(240, 248, 255);
  doc.rect(15, yPos, 180, 35, "F");
  doc.setDrawColor(41, 128, 185);
  doc.rect(15, yPos, 180, 35);

  yPos += 10;

  doc.setFontSize(11);
  doc.setTextColor(0, 0, 0);
  doc.text("MONTANTS", 20, yPos);

  yPos += 8;

  doc.setFontSize(10);
  doc.setTextColor(60, 60, 60);

  // Montant CAD
  doc.text("Montant envoyé:", 20, yPos);
  doc.setFont("helvetica", "bold");
  doc.text(`${transaction.amount_cad?.toLocaleString() || "0"} CAD`, 100, yPos);
  doc.setFont("helvetica", "normal");

  yPos += 7;

  // Frais
  doc.text("Frais:", 20, yPos);
  doc.setFont("helvetica", "bold");
  doc.text(`${transaction.fee_cad?.toLocaleString() || "0"} CAD`, 100, yPos);
  doc.setFont("helvetica", "normal");

  yPos += 7;

  // Total
  doc.setFontSize(11);
  doc.setTextColor(0, 0, 0);
  doc.text("Total:", 20, yPos);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(41, 128, 185);
  doc.text(`${transaction.total_cad?.toLocaleString() || "0"} CAD`, 100, yPos);
  doc.setFont("helvetica", "normal");

  yPos += 15;

  // Montant reçu (encadré vert)
  doc.setFillColor(240, 255, 240);
  doc.rect(15, yPos, 180, 12, "F");
  doc.setDrawColor(76, 175, 80);
  doc.rect(15, yPos, 180, 12);

  yPos += 8;

  doc.setFontSize(11);
  doc.setTextColor(0, 0, 0);
  doc.text("Montant reçu:", 20, yPos);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(76, 175, 80);
  doc.text(`${transaction.amount_fcfa?.toLocaleString() || "0"} FCFA`, 100, yPos);
  doc.setFont("helvetica", "normal");

  yPos += 15;

  // Informations du destinataire
  doc.setFontSize(12);
  doc.setTextColor(0, 0, 0);
  doc.text("DESTINATAIRE", 15, yPos);

  yPos += 8;

  const recipientData = [
    ["Nom", transaction.payout_name || "N/A"],
    ["Téléphone", transaction.payout_phonenumber || "N/A"],
    ["Opérateur", transaction.operator || "N/A"],
  ];

  autoTable(doc, {
    startY: yPos,
    head: [],
    body: recipientData,
    theme: "plain",
    styles: {
      fontSize: 10,
      cellPadding: 3,
    },
    columnStyles: {
      0: { fontStyle: "bold", textColor: [100, 100, 100], cellWidth: 50 },
      1: { textColor: [0, 0, 0] },
    },
  });

  yPos = (doc as any).lastAutoTable.finalY + 10;

  // Informations de la transaction
  doc.setFontSize(12);
  doc.setTextColor(0, 0, 0);
  doc.text("DÉTAILS DE LA TRANSACTION", 15, yPos);

  yPos += 8;

  const transactionData = [
    ["ID Transaction", transaction.id],
    ["Provider", transaction.provider || "N/A"],
    ["Type de paiement", transaction.payment_type || "N/A"],
    ["Méthode Payout", transaction.payout_method || "N/A"],
    ["Statut Payout", transaction.payout_status || "N/A"],
  ];

  autoTable(doc, {
    startY: yPos,
    head: [],
    body: transactionData,
    theme: "plain",
    styles: {
      fontSize: 9,
      cellPadding: 2,
    },
    columnStyles: {
      0: { fontStyle: "bold", textColor: [100, 100, 100], cellWidth: 60 },
      1: { textColor: [0, 0, 0], fontSize: 8 },
    },
  });

  // Pied de page
  const pageHeight = doc.internal.pageSize.height;
  doc.setFontSize(8);
  doc.setTextColor(150, 150, 150);
  doc.text("Ce reçu a été généré automatiquement par EASYPAY", 105, pageHeight - 20, { align: "center" });
  doc.text("Pour toute question, contactez notre service client", 105, pageHeight - 15, { align: "center" });
  doc.text(`Généré le ${new Date().toLocaleString("fr-FR")}`, 105, pageHeight - 10, { align: "center" });

  // Télécharger le PDF
  const fileName = `Recu_EASYPAY_${transaction.id.slice(-8)}_${Date.now()}.pdf`;
  doc.save(fileName);
};

/**
 * Charge une image et la convertit en base64
 * @param url - URL de l'image
 * @returns Promise<string | null>
 */
const loadImage = (url: string): Promise<string | null> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.drawImage(img, 0, 0);
        try {
          const dataURL = canvas.toDataURL("image/png");
          resolve(dataURL);
        } catch (e) {
          console.error("Error converting image to base64:", e);
          resolve(null);
        }
      } else {
        resolve(null);
      }
    };
    img.onerror = () => {
      console.error("Error loading image:", url);
      resolve(null);
    };
    img.src = url;
  });
};

/**
 * Retourne la couleur RGB selon le statut
 * @param status - Statut de la transaction
 * @returns Objet RGB
 */
const getStatusColor = (status: string | null | undefined): { r: number; g: number; b: number } => {
  switch (status?.toUpperCase()) {
    case "SUCCESS":
    case "COMPLETED":
      return { r: 76, g: 175, b: 80 }; // Vert
    case "PENDING":
      return { r: 255, g: 152, b: 0 }; // Orange
    case "FAILED":
    case "CANCELLED":
      return { r: 244, g: 67, b: 54 }; // Rouge
    default:
      return { r: 158, g: 158, b: 158 }; // Gris
  }
};
